import React from 'react';
import { ConsultaPlacaBasicaResult } from '../types';
import { 
  ArrowLeft, 
  Car, 
  Settings,
  Truck,
  MapPin,
  Calendar,
  Fuel,
  Hash,
  Gauge,
  Cog,
  Users,
  Download,
  Clock,
  FileText
} from 'lucide-react';

interface ConsultaPlacaBasicaResultProps {
  data: ConsultaPlacaBasicaResult;
  onBack: () => void;
}

const ConsultaPlacaBasicaResultPage: React.FC<ConsultaPlacaBasicaResultProps> = ({ data, onBack }) => {
  const formatPlate = (plate: string) => {
    // Format as AAA-9999 or AAA-9A99
    if (plate.length === 7) {
      return `${plate.slice(0, 3)}-${plate.slice(3)}`;
    }
    return plate;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
          
          <button 
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800/80 hover:bg-slate-700 text-white rounded-xl transition-all border border-slate-700/50 text-sm"
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
        </div>

        {/* Main Vehicle Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Header with Plate */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <Car className="w-10 h-10 text-white" />
                </div>
                <div>
                  <p className="text-emerald-100 text-sm font-medium">Placa do Veículo</p>
                  <h1 className="text-4xl font-black font-mono tracking-widest text-white drop-shadow-lg">
                    {formatPlate(data.placa)}
                  </h1>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-emerald-100 text-sm">{data.dadosVeiculo.marca}</p>
                <p className="text-xl font-bold text-white">{data.dadosVeiculo.modelo}</p>
              </div>
            </div>
          </div>

          {/* Consultation Info */}
          <div className="px-6 py-3 bg-slate-800/50 border-b border-slate-700/50 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-4 h-4" />
              <span>Consulta realizada em: {data.dataConsulta}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-500">
              <FileText className="w-4 h-4" />
              <span>{data.apiSource}</span>
            </div>
          </div>

          {/* Vehicle Main Info */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-2 mb-4">
              <Car className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-bold text-white">Dados do Veículo</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <InfoCard 
                icon={<Hash className="w-4 h-4" />}
                label="Chassi"
                value={data.dadosVeiculo.chassi}
                mono
              />
              <InfoCard 
                icon={<Calendar className="w-4 h-4" />}
                label="Ano Fabricação"
                value={data.dadosVeiculo.anoFabricacao}
              />
              <InfoCard 
                icon={<Calendar className="w-4 h-4" />}
                label="Ano Modelo"
                value={data.dadosVeiculo.anoModelo}
              />
              <InfoCard 
                icon={<div className="w-4 h-4 rounded-full border-2 border-current" style={{ backgroundColor: getColorCode(data.dadosVeiculo.cor) }} />}
                label="Cor"
                value={data.dadosVeiculo.cor}
              />
              <InfoCard 
                icon={<Fuel className="w-4 h-4" />}
                label="Combustível"
                value={data.dadosVeiculo.combustivel}
              />
              <InfoCard 
                icon={<Car className="w-4 h-4" />}
                label="Segmento"
                value={data.dadosVeiculo.segmento}
              />
              <InfoCard 
                icon={<MapPin className="w-4 h-4" />}
                label="Procedência"
                value={data.dadosVeiculo.procedencia}
              />
              <InfoCard 
                icon={<MapPin className="w-4 h-4" />}
                label="Município/UF"
                value={`${data.dadosVeiculo.municipio}/${data.dadosVeiculo.uf}`}
              />
            </div>
          </div>

          {/* Technical Data */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-blue-400" />
              <h2 className="text-lg font-bold text-white">Dados Técnicos</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <InfoCard 
                icon={<Car className="w-4 h-4" />}
                label="Tipo de Veículo"
                value={data.dadosTecnicos.tipoVeiculo}
                accent="blue"
              />
              <InfoCard 
                icon={<Cog className="w-4 h-4" />}
                label="Sub-segmento"
                value={data.dadosTecnicos.subSegmento}
                accent="blue"
              />
              <InfoCard 
                icon={<Hash className="w-4 h-4" />}
                label="Número do Motor"
                value={data.dadosTecnicos.numeroMotor}
                mono
                accent="blue"
              />
              <InfoCard 
                icon={<Cog className="w-4 h-4" />}
                label="Caixa de Câmbio"
                value={data.dadosTecnicos.numeroCaixaCambio}
                mono
                accent="blue"
              />
              <InfoCard 
                icon={<Gauge className="w-4 h-4" />}
                label="Potência"
                value={data.dadosTecnicos.potencia}
                accent="blue"
              />
              <InfoCard 
                icon={<Gauge className="w-4 h-4" />}
                label="Cilindradas"
                value={data.dadosTecnicos.cilindradas}
                accent="blue"
              />
            </div>
          </div>

          {/* Cargo/Capacity Data */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5 text-amber-400" />
              <h2 className="text-lg font-bold text-white">Capacidade</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoCard 
                icon={<Truck className="w-4 h-4" />}
                label="Número de Eixos"
                value={data.dadosCarga.numeroEixos}
                accent="amber"
              />
              <InfoCard 
                icon={<Truck className="w-4 h-4" />}
                label="Cap. Máxima de Tração"
                value={data.dadosCarga.capacidadeMaximaTracao}
                accent="amber"
              />
              <InfoCard 
                icon={<Users className="w-4 h-4" />}
                label="Cap. de Passageiros"
                value={`${data.dadosCarga.capacidadePassageiro} pessoas`}
                accent="amber"
                highlight
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

// Info Card Component
interface InfoCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  mono?: boolean;
  accent?: 'emerald' | 'blue' | 'amber';
  highlight?: boolean;
}

const InfoCard: React.FC<InfoCardProps> = ({ 
  icon, 
  label, 
  value, 
  mono = false,
  accent = 'emerald',
  highlight = false
}) => {
  const accentColors = {
    emerald: 'text-emerald-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
  };

  return (
    <div className={`
      p-4 rounded-xl transition-all
      ${highlight 
        ? 'bg-gradient-to-br from-slate-700/50 to-slate-800/50 border border-slate-600/50' 
        : 'bg-slate-800/40 hover:bg-slate-800/60'
      }
    `}>
      <div className={`flex items-center gap-2 ${accentColors[accent]} text-xs mb-2`}>
        {icon}
        <span className="text-slate-400">{label}</span>
      </div>
      <p className={`
        text-white font-medium truncate
        ${mono ? 'font-mono text-sm' : ''}
      `} title={value}>
        {value || 'N/A'}
      </p>
    </div>
  );
};

// Helper function to get approximate color code
function getColorCode(colorName: string): string {
  const colors: Record<string, string> = {
    'branca': '#ffffff',
    'branco': '#ffffff',
    'preta': '#1a1a1a',
    'preto': '#1a1a1a',
    'prata': '#c0c0c0',
    'cinza': '#808080',
    'vermelha': '#dc2626',
    'vermelho': '#dc2626',
    'azul': '#2563eb',
    'verde': '#16a34a',
    'amarela': '#facc15',
    'amarelo': '#facc15',
    'marrom': '#78350f',
    'bege': '#d4b896',
    'laranja': '#ea580c',
    'rosa': '#ec4899',
    'roxa': '#9333ea',
    'roxo': '#9333ea',
  };
  
  const normalizedColor = colorName.toLowerCase().trim();
  return colors[normalizedColor] || 'transparent';
}

export default ConsultaPlacaBasicaResultPage;

